import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import { ajustarEstoque, listarEstoque, type LinhaEstoque } from '../lib/consultas';
import { paraNumero } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// Tela 13 — Estoque. Só o dono chega aqui.
//
// O que ela é: a prateleira inteira numa lista, com a quantidade editável.
// O que ela NÃO é: uma tela de entrada de mercadoria. Entrada tem nota, custo e
// fornecedor e mora em /entrada. Aqui é conferência — contou, corrigiu.
//
// O AJUSTE PASSA PELA RPC ajustar_estoque, e isso não é detalhe: ela grava o
// delta em stock_movements com motivo 'ajuste'. Um update direto em `stock`
// mudaria o saldo sem deixar rastro, e no mês seguinte ninguém saberia dizer se
// a peça sumiu, foi vendida sem registro ou foi digitada errada aqui.
//
// A quantidade digitada é a CONTADA, não a diferença — é o mesmo contrato da
// RPC, e é como se conta prateleira: olha, conta, escreve o que viu. Ela também
// zera o pendente_registro (migração 015), porque quem contou a prateleira
// contou a verdade inteira, inclusive o que saiu sem registro.

type Ordem = 'sku' | 'descricao' | 'quantidade' | 'falta';

export default function Estoque() {
  const cliente = useQueryClient();
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>('sku');
  const [soFalta, setSoFalta] = useState(false);

  const lista = useQuery({ queryKey: ['estoque'], queryFn: () => listarEstoque() });

  const linhas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    let r = (lista.data ?? []).filter((l) => (
      !termo
      || l.sku.toLowerCase().includes(termo)
      || l.descricao.toLowerCase().includes(termo)
      || (l.categoria ?? '').toLowerCase().includes(termo)
    ));
    if (soFalta) r = r.filter((l) => l.quantidade <= l.estoque_min);
    return [...r].sort((a, b) => {
      if (ordem === 'quantidade') return a.quantidade - b.quantidade;
      if (ordem === 'descricao') return a.descricao.localeCompare(b.descricao);
      // 'falta' = quão abaixo do mínimo está, o mais crítico primeiro.
      if (ordem === 'falta') {
        return (a.quantidade - a.estoque_min) - (b.quantidade - b.estoque_min);
      }
      return a.sku.localeCompare(b.sku);
    });
  }, [lista.data, busca, ordem, soFalta]);

  const abaixoDoMinimo = (lista.data ?? []).filter((l) => l.quantidade <= l.estoque_min).length;
  const pendentes = (lista.data ?? []).filter((l) => l.pendente_registro > 0).length;

  return (
    <Layout titulo="Estoque">
      <p className="mb-3 text-sm text-mute">
        A prateleira inteira. Digite a quantidade <strong className="text-paper">contada</strong>,
        não a diferença — o sistema calcula o ajuste e guarda o histórico.
        Mercadoria que chegou com nota entra por <Link className="underline" to="/entrada">Entrada</Link>,
        não aqui.
      </p>

      {(abaixoDoMinimo > 0 || pendentes > 0) && (
        <div className="mb-3">
          <Aviso tipo="alerta">
            {abaixoDoMinimo > 0 && <>{abaixoDoMinimo} peça(s) no mínimo ou abaixo. </>}
            {pendentes > 0 && (
              <>{pendentes} com saída sem registro pendente — contar zera a pendência.</>
            )}
          </Aviso>
        </div>
      )}

      <input
        className="campo"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Filtrar por SKU, descrição ou categoria"
        autoComplete="off"
      />

      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-dim">Ordenar</span>
          <select
            className="campo py-1"
            value={ordem}
            onChange={(e) => setOrdem(e.target.value as Ordem)}
          >
            <option value="sku">SKU</option>
            <option value="descricao">Descrição</option>
            <option value="quantidade">Menor quantidade</option>
            <option value="falta">Mais abaixo do mínimo</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={soFalta} onChange={(e) => setSoFalta(e.target.checked)} />
          <span>Só o que está no mínimo</span>
        </label>
      </div>

      {lista.isLoading && <Carregando />}
      {lista.error && (
        <div className="mt-3"><Aviso tipo="erro">{mensagemDeErro(lista.error)}</Aviso></div>
      )}

      <p className="mt-4 text-xs text-dim">
        {linhas.length} de {(lista.data ?? []).length} peça(s) com estoque.
      </p>

      <div className="mt-2 space-y-2">
        {linhas.map((l) => (
          <LinhaAjuste
            key={l.product_id}
            linha={l}
            aoAjustar={() => cliente.invalidateQueries({ queryKey: ['estoque'] })}
          />
        ))}
        {!lista.isLoading && linhas.length === 0 && (
          <p className="text-sm text-dim">Nenhuma peça com esse filtro.</p>
        )}
      </div>
    </Layout>
  );
}

function LinhaAjuste({ linha: l, aoAjustar }: {
  linha: LinhaEstoque; aoAjustar: () => void;
}) {
  const [contado, setContado] = useState('');
  const [ok, setOk] = useState(false);

  // paraNumero e não Number(): "1.500" em pt-BR é mil e quinhentos, e Number()
  // devolveria 1,5. Regra 9 do CLAUDE.md, com teste de regressão.
  const valor = contado.trim() ? paraNumero(contado) : null;
  const invalido = valor !== null && (Number.isNaN(valor) || valor < 0);
  const delta = valor !== null && !invalido ? valor - l.quantidade : null;

  const ajustar = useMutation({
    mutationFn: () => ajustarEstoque({
      productId: l.product_id,
      novaQuantidade: valor!,
      motivo: 'ajuste',
      observacao: 'Conferência pela tela de estoque',
    }),
    onSuccess: () => {
      setContado('');
      setOk(true);
      aoAjustar();
    },
  });

  const critico = l.quantidade <= l.estoque_min;

  return (
    <div className="cartao">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/peca/${l.product_id}`} className="font-mono text-sm text-paper underline">
            {l.sku}
          </Link>
          <p className="truncate text-sm text-mute">{l.descricao}</p>
          <p className="text-xs text-dim">
            {l.local ?? 'sem local'}
            {l.categoria && ` · ${l.categoria}`}
            {!l.ativo && ' · desativada'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {/* Vermelho aqui é ALERTA de prateleira, não prejuízo — a regra de
              marca que proíbe vermelho para saldo vale nas telas de dinheiro. */}
          <p className={`text-lg font-semibold ${critico ? 'text-erro' : 'text-paper'}`}>
            {l.quantidade}
          </p>
          <p className="text-xs text-dim">mín. {l.estoque_min}</p>
        </div>
      </div>

      {l.pendente_registro > 0 && (
        <p className="mt-2 text-xs text-alerta">
          {l.pendente_registro} unidade(s) saíram sem registro. Contar zera essa pendência.
        </p>
      )}

      <div className="mt-3 flex items-end gap-2">
        <div className="flex-1">
          <label className="rotulo" htmlFor={`contado-${l.product_id}`}>Quantidade contada</label>
          <input
            id={`contado-${l.product_id}`}
            className="campo"
            value={contado}
            inputMode="decimal"
            autoComplete="off"
            placeholder={String(l.quantidade)}
            onChange={(e) => { setContado(e.target.value); setOk(false); }}
          />
        </div>
        <button
          type="button"
          className="botao-secundario px-4 py-2"
          disabled={valor === null || invalido || ajustar.isPending}
          onClick={() => ajustar.mutate()}
        >
          {ajustar.isPending ? '...' : 'Ajustar'}
        </button>
      </div>

      {invalido && (
        <p className="mt-2 text-xs text-erro">Quantidade inválida.</p>
      )}
      {delta !== null && delta !== 0 && (
        <p className="mt-2 text-xs text-dim">
          Vai registrar {delta > 0 ? `entrada de ${delta}` : `saída de ${Math.abs(delta)}`} no histórico.
        </p>
      )}
      {ok && <p className="mt-2 text-xs text-ok">Estoque ajustado.</p>}
      {ajustar.error && (
        <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(ajustar.error)}</Aviso></div>
      )}
    </div>
  );
}
